import { useMutation, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { documentosApi } from "@/features/documentos/services/documentos.api";
import { qk } from "@/lib/query-keys";
import { toastErrorApi, toastExito } from "@/lib/toast";
import type { CategoriaDocumento, DocumentoApi, DocumentoVinculado } from "@/lib/types";
import { aDocumentoVinculado } from "./use-documentos-solicitud";

interface SubirArgs {
  numeroCfdi: number;
  categoria: CategoriaDocumento;
  /** Archivo local · o, si viene de un ZIP, `deZip` con su ubicación en el área temporal. */
  file?: File;
  deZip?: { loteId: string; archivoId: string; nombre: string };
  /** Tipo del catálogo (P31) · obligatorio cuando categoria === "Adicional". */
  tipoDocumento?: string;
  /** Fase 4 · fecha de emisión (yyyy-MM-dd) · obligatoria si el documento maneja vigencia. */
  fechaEmision?: string;
}

interface SubirMutationContext {
  previous: DocumentoVinculado[] | null;
  tempId: string | null;
}

interface EliminarMutationContext {
  previous: DocumentoVinculado[] | null;
}

/**
 * Mutación de subida con **optimistic update** sobre la lista de documentos de la solicitud
 * (Gestión Documental · fuente única): el documento aparece al instante con un id temporal y se
 * reemplaza por el real con la respuesta del servidor (rollback si falla).
 */
export function useSubirDocumento(solicitudId: string, categoriaLabel: string) {
  const qc = useQueryClient();
  return useMutation<DocumentoApi, Error, SubirArgs, SubirMutationContext>({
    mutationFn: ({ numeroCfdi, categoria, file, deZip, tipoDocumento, fechaEmision }) =>
      deZip
        ? documentosApi.subirArchivoDeZip(solicitudId, deZip.loteId, deZip.archivoId, numeroCfdi, tipoDocumento ?? "", fechaEmision)
        : documentosApi.subir(solicitudId, numeroCfdi, categoria, file!, tipoDocumento, fechaEmision),

    onMutate: async ({ numeroCfdi, categoria, file, deZip, tipoDocumento }) => {
      const queryKey = qk.documentosSolicitud(solicitudId);
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<DocumentoVinculado[]>(queryKey) ?? null;
      if (!previous) return { previous: null, tempId: null };

      const tempId = `temp-${crypto.randomUUID()}`;
      const optimistic: DocumentoVinculado = {
        id: tempId,
        documentoId: tempId,
        categoria,
        nombreOriginal: deZip?.nombre ?? file?.name ?? "",
        vinculadoAt: new Date().toISOString(),
        tipoDocumento: tipoDocumento ?? null,
        numeroCfdi,
      };
      qc.setQueryData<DocumentoVinculado[]>(queryKey, [...previous, optimistic]);
      return { previous, tempId };
    },

    onSuccess: (doc, _vars, context) => {
      const real = aDocumentoVinculado(doc);
      qc.setQueryData<DocumentoVinculado[]>(qk.documentosSolicitud(solicitudId), (actual) =>
        actual && context?.tempId ? actual.map((d) => (d.id === context.tempId ? real : d)) : actual
      );
      invalidarExpediente(qc, solicitudId);
      toastExito({ title: `${categoriaLabel} subido`, message: doc.nombreOriginal });
    },

    onError: (e, _vars, context) => {
      rollback(qc, solicitudId, context?.previous ?? null);
      toastErrorApi(e);
    },
  });
}

/**
 * Mutación de eliminación con optimistic update.
 * Mismo patrón que subir · removemos del cache al instante y reconciliamos
 * (o hacemos rollback en error).
 */
export function useEliminarDocumento(solicitudId: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string, EliminarMutationContext>({
    mutationFn: (documentoId) => documentosApi.eliminar(solicitudId, documentoId),

    onMutate: async (documentoId) => {
      const queryKey = qk.documentosSolicitud(solicitudId);
      await qc.cancelQueries({ queryKey });
      const previous = qc.getQueryData<DocumentoVinculado[]>(queryKey) ?? null;
      if (!previous) return { previous: null };
      qc.setQueryData<DocumentoVinculado[]>(queryKey, previous.filter((d) => d.documentoId !== documentoId));
      return { previous };
    },

    onSuccess: () => {
      invalidarExpediente(qc, solicitudId);
      toastExito({ title: "Documento eliminado", message: "Se eliminó el archivo." });
    },

    onError: (e, _vars, context) => {
      rollback(qc, solicitudId, context?.previous ?? null);
      toastErrorApi(e);
    },
  });
}

export function useDescargarDocumento(solicitudId: string) {
  return useMutation({
    mutationFn: ({ id, nombre, version }: { id: string; nombre: string; version?: number }) =>
      documentosApi.descargar(solicitudId, id, nombre, version),
    onError: toastErrorApi,
  });
}

/**
 * Tras subir o eliminar · los requisitos y el expediente (revisión, vigencia) se re-evalúan, y el
 * detalle se refresca (el primer documento pasa la solicitud a «Adjuntando documentos»).
 */
function invalidarExpediente(qc: QueryClient, solicitudId: string) {
  void qc.invalidateQueries({ queryKey: qk.requisitosExpediente(solicitudId) });
  void qc.invalidateQueries({ queryKey: qk.expediente(solicitudId) });
  void qc.invalidateQueries({ queryKey: qk.solicitud(solicitudId) });
}

function rollback(qc: QueryClient, solicitudId: string, previous: DocumentoVinculado[] | null) {
  if (previous) qc.setQueryData(qk.documentosSolicitud(solicitudId), previous);
}

export function useReemplazarDocumento(solicitudId: string, documentoId: string) {
  const qc = useQueryClient();
  return useMutation<DocumentoApi, Error, { file: File; motivo: string; fechaEmision?: string }>({
    mutationFn: ({ file, motivo, fechaEmision }) => documentosApi.reemplazar(solicitudId, documentoId, file, motivo, fechaEmision),
    onSuccess: (doc) => {
      qc.setQueryData<DocumentoVinculado[]>(qk.documentosSolicitud(solicitudId), (actual) =>
        actual?.map((d) => (d.documentoId === documentoId ? { ...d, nombreOriginal: doc.nombreOriginal } : d))
      );
      void qc.invalidateQueries({ queryKey: qk.versionesDocumento(documentoId) });
      void qc.invalidateQueries({ queryKey: qk.expediente(solicitudId) });
      toastExito({ title: "Documento reemplazado", message: `Se guardó la versión ${doc.versionActual}. La anterior se conserva en el historial.` });
    },
    onError: (err) => toastErrorApi(err),
  });
}
